import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { CategorySummary, EventCard as EventCardData } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Search, SearchX } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms, pluralize } from '@/lib/format'
import { listCategories, listCities, listPublicEvents } from '@/lib/events'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { EventCard } from '@/components/events/event-card'
import { CategoryIcon } from '@/components/events/category-icon'
import { Button, buttonVariants } from '@/components/ui/button'
import { Select } from '@/components/ui/input'

import { EVENTS_PER_PAGE } from '@/constants/platform'

interface Filters {
    q: string
    category: string
    city: string
    when: string
    price: string
}

interface EventsPageProps {
    events: Array<EventCardData>
    total: number
    page: number
    pageCount: number
    categories: Array<CategorySummary>
    cities: Array<string>
    filters: Filters
}

const WHEN_OPTIONS = [
    { value: '', label: 'Any date' },
    { value: 'week', label: 'Next 7 days' },
    { value: 'month', label: 'Next 30 days' },
    { value: 'quarter', label: 'Next 3 months' }
]

const PRICE_OPTIONS = [
    { value: '', label: 'Any price' },
    { value: 'free', label: 'Free' },
    { value: 'paid', label: 'Paid' }
]

const buildQuery = (filters: Partial<Filters> & { page?: number }): Record<string, string> => {
    const query: Record<string, string> = {}

    for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== '' && !(key === 'page' && value === 1)) query[key] = String(value)
    }

    return query
}

const EventsPage: FC<EventsPageProps> = ({ events, total, page, pageCount, categories, cities, filters }): ReactNode => {
    const router = useRouter()
    const [search, setSearch] = useState(filters.q)

    useEffect(() => {
        setSearch(filters.q)
    }, [filters.q])

    const update = (changes: Partial<Filters>) => {
        router.push({ pathname: '/events', query: buildQuery({ ...filters, ...changes }) })
    }

    const onSearch = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        update({ q: search.trim() })
    }

    const activeCategory = categories.find(category => category.slug === filters.category)
    const hasFilters = Boolean(filters.q || filters.category || filters.city || filters.when || filters.price)

    return (
        <Layout>
            <Seo
                title={activeCategory ? `${activeCategory.name} events` : 'Browse events'}
                description='Browse upcoming concerts, conferences, workshops, food experiences and meetups on Turnout, and get your tickets in a minute.'
            />

            <section id='browse-hero' className='border-b border-border bg-card'>
                <div className='mx-auto flex max-w-7xl flex-col gap-8 px-4 py-12 sm:px-6 lg:px-8'>
                    <div className='flex flex-col gap-3'>
                        <h1 className='text-4xl font-extrabold sm:text-5xl'>
                            {activeCategory
                                ? <span data-mdlfy-cms={cms('categories', activeCategory.id, 'name')}>{activeCategory.name}</span>
                                : 'Browse events'}
                        </h1>
                        <p className='max-w-2xl text-lg text-muted-foreground'>
                            {activeCategory?.description
                                ? <span data-mdlfy-cms={cms('categories', activeCategory.id, 'description')}>{activeCategory.description}</span>
                                : 'Concerts, conferences, classes and meetups, all with instant QR tickets.'}
                        </p>
                    </div>

                    <form onSubmit={onSearch} action='/events' method='get' className='flex flex-col gap-3 lg:flex-row' role='search'>
                        <div className='relative flex-1'>
                            <label htmlFor='events-search' className='sr-only'>Search events</label>
                            <Search className='pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                            <input
                                id='events-search'
                                name='q'
                                type='search'
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                                placeholder='Search by event, venue, city or organizer'
                                className='h-12 w-full rounded-full border border-input bg-background pl-12 pr-4 text-base focus-visible:border-primary focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/20'
                            />
                        </div>

                        <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:flex'>
                            <div className='lg:w-44'>
                                <label htmlFor='filter-city' className='sr-only'>City</label>
                                <Select id='filter-city' name='city' value={filters.city} onChange={event => update({ city: event.target.value })} className='h-12 rounded-full'>
                                    <option value=''>All cities</option>
                                    {cities.map(city => (
                                        <option key={city} value={city}>{city}</option>
                                    ))}
                                </Select>
                            </div>

                            <div className='lg:w-44'>
                                <label htmlFor='filter-when' className='sr-only'>Date</label>
                                <Select id='filter-when' name='when' value={filters.when} onChange={event => update({ when: event.target.value })} className='h-12 rounded-full'>
                                    {WHEN_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </Select>
                            </div>

                            <div className='lg:w-36'>
                                <label htmlFor='filter-price' className='sr-only'>Price</label>
                                <Select id='filter-price' name='price' value={filters.price} onChange={event => update({ price: event.target.value })} className='h-12 rounded-full'>
                                    {PRICE_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </Select>
                            </div>

                            <Button type='submit' size='lg' className='col-span-2 sm:col-span-3 lg:col-span-1'>
                                Search
                            </Button>
                        </div>

                        {filters.category && <input type='hidden' name='category' value={filters.category} />}
                    </form>

                    <nav aria-label='Categories' className='no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1'>
                        <button
                            type='button'
                            onClick={() => update({ category: '' })}
                            className={cn(
                                'shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors',
                                !filters.category ? 'border-ink bg-ink text-ink-foreground' : 'border-border bg-background hover:border-foreground/30'
                            )}
                            aria-pressed={!filters.category}
                        >
                            All
                        </button>

                        {categories.map(category => (
                            <button
                                key={category.id}
                                type='button'
                                onClick={() => update({ category: category.slug })}
                                className={cn(
                                    'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors',
                                    filters.category === category.slug ? 'border-ink bg-ink text-ink-foreground' : 'border-border bg-background hover:border-foreground/30'
                                )}
                                aria-pressed={filters.category === category.slug}
                            >
                                <CategoryIcon slug={category.slug} className='size-4' />
                                <span data-mdlfy-cms={cms('categories', category.id, 'name')}>{category.name}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            </section>

            <section id='results' className='mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8'>
                <div className='mb-6 flex flex-wrap items-center justify-between gap-3'>
                    <p className='text-sm text-muted-foreground' aria-live='polite'>
                        {total} upcoming {pluralize(total, 'event')}
                        {filters.q && <> matching &ldquo;{filters.q}&rdquo;</>}
                    </p>

                    {hasFilters && (
                        <Link href='/events' className='text-sm font-semibold text-primary hover:underline'>
                            Clear all filters
                        </Link>
                    )}
                </div>

                {events.length > 0
                    ? (
                        <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {events.map(event => (
                                <EventCard key={event.id} event={event} />
                            ))}
                        </div>
                    )
                    : (
                        <div className='flex flex-col items-center gap-4 rounded-3xl border border-dashed border-border px-6 py-20 text-center'>
                            <span className='flex size-14 items-center justify-center rounded-2xl bg-muted'>
                                <SearchX className='size-6 text-muted-foreground' aria-hidden='true' />
                            </span>
                            <h2 className='text-xl font-bold'>No events match those filters</h2>
                            <p className='max-w-md text-muted-foreground'>
                                Try a different date range or city, or clear the filters to see everything coming up.
                            </p>
                            <Link href='/events' className={buttonVariants({ variant: 'outline' })}>
                                Show all events
                            </Link>
                        </div>
                    )}

                {pageCount > 1 && (
                    <nav aria-label='Pagination' className='mt-12 flex items-center justify-center gap-3'>
                        {page > 1
                            ? (
                                <Link
                                    href={{ pathname: '/events', query: buildQuery({ ...filters, page: page - 1 }) }}
                                    className={buttonVariants({ variant: 'outline', size: 'sm' })}
                                >
                                    <ChevronLeft className='size-4' aria-hidden='true' />
                                    Previous
                                </Link>
                            )
                            : <span className={buttonVariants({ variant: 'outline', size: 'sm', className: 'pointer-events-none opacity-40' })} aria-disabled='true'><ChevronLeft className='size-4' aria-hidden='true' />Previous</span>}

                        <span className='text-sm text-muted-foreground'>
                            Page {page} of {pageCount}
                        </span>

                        {page < pageCount
                            ? (
                                <Link
                                    href={{ pathname: '/events', query: buildQuery({ ...filters, page: page + 1 }) }}
                                    className={buttonVariants({ variant: 'outline', size: 'sm' })}
                                >
                                    Next
                                    <ChevronRight className='size-4' aria-hidden='true' />
                                </Link>
                            )
                            : <span className={buttonVariants({ variant: 'outline', size: 'sm', className: 'pointer-events-none opacity-40' })} aria-disabled='true'>Next<ChevronRight className='size-4' aria-hidden='true' /></span>}
                    </nav>
                )}
            </section>
        </Layout>
    )
}

const readParam = (value: string | Array<string> | undefined): string => {
    const raw = Array.isArray(value) ? value[0] : value
    return (raw ?? '').trim().slice(0, 100)
}

export const getServerSideProps: GetServerSideProps<EventsPageProps> = async ({ query }) => {
    const filters: Filters = {
        q: readParam(query.q),
        category: readParam(query.category),
        city: readParam(query.city),
        when: readParam(query.when),
        price: readParam(query.price)
    }

    const page = Math.max(1, Number.parseInt(readParam(query.page) || '1', 10) || 1)

    try {
        const [result, categories, cities] = await Promise.all([
            listPublicEvents({ ...filters, limit: EVENTS_PER_PAGE, offset: (page - 1) * EVENTS_PER_PAGE }),
            listCategories(),
            listCities()
        ])

        return {
            props: {
                events: result.events,
                total: result.total,
                page,
                pageCount: Math.max(1, Math.ceil(result.total / EVENTS_PER_PAGE)),
                categories,
                cities,
                filters
            }
        }
    }
    catch (error) {
        console.error('Events page data failed to load:', error instanceof Error ? error.message : error)
        return { props: { events: [], total: 0, page: 1, pageCount: 1, categories: [], cities: [], filters } }
    }
}

export default EventsPage
